/**
 * @description Header fixo do layout privado (web e mobile).
 * @module LayoutPrivateHeader
 */

import { Menu, MenuOpen } from "@mui/icons-material";
import {
  AppBar,
  Box,
  IconButton,
  Stack,
  Toolbar,
  useTheme,
} from "@mui/material";

import { currentFeatures } from "@/configs/Settings";
import { useSessionContext } from "@/contexts/SessionContext";
import { SncrHeaderConnect } from "src-next/modules/sncr";

import { AvatarMenu } from "./AvatarMenu";
import { HeaderBrand } from "./HeaderBrand";
import { HeaderContractWarnings } from "./HeaderContractWarnings";
import { HeaderNotification } from "./HeaderNotification";
import { HeaderOrganizationSelect } from "./HeaderOrganizationSelect";
import { SignatureControls } from "./SignatureControls";
import {
  MOBILE_HEADER_TOP_GAP,
  getMobileHeaderElevatedZIndex,
  getMobileToolbarHeight,
} from "./headerLayoutUtils";
import { useHeaderCollapse } from "./HeaderCollapseContext";
import type { LayoutPrivateHeaderContentProps } from "./types";
import { TeleconsultoriaAvailabilityHeaderToggle } from "src-next/modules/teleconsultoria/presence/TeleconsultoriaAvailabilityHeaderToggle";

export function LayoutPrivateHeaderContent({
  handleDrawerToggle,
  avatarMenuAnchorEl,
  handleMenuOpen,
  handleMenuClose,
  isMediumScreen,
  isSidebarOpen,
}: LayoutPrivateHeaderContentProps) {
  const theme = useTheme();
  const { roles, access } = useSessionContext();
  const { isHeaderCollapsed, isHeaderPanelOpen, closeMobileHeaderPanel } =
    useHeaderCollapse();

  const isMobile = !isMediumScreen;
  const isProfessionalFluxmed =
    access?.type === "professional" && currentFeatures?.defaultApp;

  const showExpandedMobileContent = !isMobile || !isHeaderCollapsed;
  const mobileToolbarHeight = getMobileToolbarHeight(isHeaderCollapsed, theme);
  const headerBackground = currentFeatures?.defaultApp
    ? "primary800.main"
    : "#00A884";
  const isCollapsedMobile = isMobile && isHeaderCollapsed;
  const isExpandedHeader = !isCollapsedMobile;
  const headerElevated = isMobile && isHeaderPanelOpen;
  const headerZIndex = headerElevated
    ? getMobileHeaderElevatedZIndex(theme)
    : theme.zIndex.drawer + 1;

  const handleHeaderSurfaceClick = () => {
    if (isMobile && isHeaderPanelOpen) {
      closeMobileHeaderPanel();
    }
  };

  return (
    <AppBar
      position={isMobile ? "fixed" : "sticky"}
      elevation={0}
      onClick={handleHeaderSurfaceClick}
      sx={{
        borderRadius: isCollapsedMobile ? 999 : 0,
        overflow:
          isMobile && isExpandedHeader && !isHeaderPanelOpen
            ? "hidden"
            : undefined,
        ...(isMobile
          ? {
              top: isCollapsedMobile ? MOBILE_HEADER_TOP_GAP : 0,
              width: isCollapsedMobile ? "auto" : "100%",
              maxWidth: isCollapsedMobile ? "calc(100% - 16px)" : undefined,
              left: isCollapsedMobile ? "auto" : 0,
              right: isCollapsedMobile ? 8 : 0,
              boxShadow: isCollapsedMobile
                ? "none"
                : "none",
              transform: isCollapsedMobile ? "none" : "none",
            }
          : {
              top: 0,
              width: "100%",
              left: "auto",
              right: "auto",
              transform: "none",
              boxShadow: "none",
              flexShrink: 0,
            }),
        px: 0,
        bgcolor: isCollapsedMobile ? "transparent" : headerBackground,
        backgroundColor: isCollapsedMobile ? "transparent" : headerBackground,
        backgroundImage: "none",
        color: "common.white",
        zIndex: headerZIndex,
        transition: theme.transitions.create(
          ["width", "border-radius", "right", "left", "transform", "z-index"],
          {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shorter,
          }
        ),
      }}
    >
      <Toolbar
        onClick={handleHeaderSurfaceClick}
        sx={{
          minHeight: isMobile ? mobileToolbarHeight : undefined,
          width: isCollapsedMobile ? "auto" : undefined,
          px: isCollapsedMobile ? 0 : undefined,
          justifyContent: isCollapsedMobile ? "flex-end" : undefined,
          gap: isCollapsedMobile ? 0.25 : undefined,
          bgcolor: isCollapsedMobile ? "transparent" : "inherit",
          backgroundImage: "none",
        }}
      >
        {!isMobile && !currentFeatures.register ? (
          <IconButton
            color="inherit"
            onClick={handleDrawerToggle}
            edge="start"
            aria-label={isSidebarOpen ? "Fechar menu" : "Abrir menu"}
            sx={{ mr: 2 }}
          >
            {isSidebarOpen ? <MenuOpen /> : <Menu />}
          </IconButton>
        ) : null}

        {showExpandedMobileContent ? (
          <>
            <Stack direction="row" alignItems="center" spacing={3}>
              <HeaderBrand />

              {isProfessionalFluxmed ? <SignatureControls /> : null}

              {isProfessionalFluxmed ? <SncrHeaderConnect /> : null}

              {isProfessionalFluxmed ? <HeaderContractWarnings /> : null}
            </Stack>

            <Box sx={{ flexGrow: 1 }} />

            {roles &&
              roles.length > 0 &&
              access?.organizationId &&
              !currentFeatures.register && <HeaderOrganizationSelect />}
          </>
        ) : null}

        <Box
          onClick={(event) => event.stopPropagation()}
          sx={{
            display: "flex",
            alignItems: "center",
            ml: isCollapsedMobile ? "auto" : undefined,
            ...(isCollapsedMobile && {
              borderRadius: 999,
              pl: 0.25,
              pr: 0.25,
              py: 0.25,
              bgcolor: headerBackground,
              boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.3)",
            }),
          }}
        >
          {/* Só aparece para profissional da prestadora de teleconsultoria. */}
          <TeleconsultoriaAvailabilityHeaderToggle />

          <HeaderNotification isMobile={isMobile} />

          <AvatarMenu
            isMobile={isMobile}
            anchorEl={avatarMenuAnchorEl}
            handleMenuOpen={handleMenuOpen}
            handleMenuClose={handleMenuClose}
          />
        </Box>
      </Toolbar>
    </AppBar>
  );
}
